<template>
  <div>
    <div v-if="loading">
      <b-spinner
        variant="primary"
        style="width: 3rem; height: 3rem"
        label="Large Spinner"
      ></b-spinner>
    </div>

    <b-row>
      <b-col>
        <b-card>
          <div class="row">
            <div class="col">
              <h4>{{ property.title }}</h4>
            </div>
            <div class="col-auto text-right">
              <a
                v-b-tooltip.hover
                title="Print"
                :href="
                  '/pdf/reports/property/' +
                  id +
                  '/report?cid=' +
                  property.company_id
                "
                class="btn btn-primary mr-1"
                target="_blank"
              >
                <i class="lni lni-printer mr-0"></i>
              </a>

              <router-link
                v-b-tooltip.hover
                title="Edit"
                :to="{
                  name: 'properties-edit',
                  params: { id: property.id },
                }"
                class="btn btn-info mr-1"
              >
                <i class="lni lni-pencil mr-0"></i>
              </router-link>

              <span
                v-b-tooltip.hover
                style="cursor: pointer"
                title="Delete"
                @click="confirmDelete(property.id)"
                class="btn btn-danger"
              >
                <i class="lni lni-trash mr-0"></i>
              </span>
            </div>
          </div>

          <div style="clear: both"></div>

          <b-tabs v-model="tabIndex" class="mt-2">
            <b-tab title="Property" class="pt-3">
              <div class="row">
                <div class="col-md-6">
                  <table class="table">
                    <table-row label="Occupied" :noBorder="true">
                      <i
                        class="lni lni-checkmark text-success"
                        v-if="property.is_occupied"
                      ></i>
                      <i
                        class="lni lni-cross-circle text-danger"
                        v-if="!property.is_occupied"
                      ></i>
                    </table-row>

                    <table-row label="Title">
                      {{ property.title }}
                    </table-row>

                    <table-row label="Description">
                      {{ property.description }}
                    </table-row>

                    <table-row
                      label="Address"
                      class="nl2br"
                      :copyText="property.address"
                    >
                      {{ property.address }}
                    </table-row>

                    <table-row label="City" :copyText="property.city">
                      {{ property.city }}
                    </table-row>

                    <table-row
                      label="Postal Code"
                      :copyText="property.postal_code"
                    >
                      {{ property.postal_code }}
                    </table-row>

                    <table-row label="Province">
                      {{ property.province }}
                    </table-row>

                    <table-row label="Country">
                      {{ property.country }}
                    </table-row>

                    <table-row label="Collecitons">
                      <b-badge
                        variant="info"
                        v-for="c in property.collections"
                        :key="c.id"
                        class="m-0"
                        >{{ c.title }}</b-badge
                      >
                    </table-row>

                    <table-row icon="lni-database" :noBorder="true" />

                    <table-row label="Id">
                      {{ property.id }}
                    </table-row>

                    <table-row label="Created">
                      {{
                        moment(new Date(property.created_at)).format(
                          "DD MMM YYYY, HH:mm:ss"
                        )
                      }}
                    </table-row>
                    <table-row label="Updated">
                      {{
                        moment(new Date(property.updated_at)).format(
                          "DD MMM YYYY, HH:mm:ss"
                        )
                      }}
                    </table-row>
                  </table>
                </div>
                <div class="col-md-6">
                  <div class="card" style="max-height: 600px" v-if="chartReady">
                    <div class="card-body d-block">
                      <bar-chart
                        :chart-data="chartData"
                        :options="chartOptions"
                      ></bar-chart>
                    </div>
                  </div>
                </div>
              </div>
            </b-tab>
            <b-tab title="Images" class="pt-3">
              <b-skeleton-img v-if="ImageLoading"></b-skeleton-img>

              <v-lazy-image
                @intersect="ImageLoading = true"
                @load="ImageLoading = false"
                v-if="selectedImage != null"
                :src="selectedImage"
                src-placeholder="@/assets/img/loading.png"
                class="img-fluid border"
              />
              <div></div>

              <div
                class="border d-inline-block p-1 m-1"
                v-for="image in property.property_images"
                :key="image.id"
              >
                <a href="#" @click="selectedImage = image.large">
                  <v-lazy-image :src="image.small" class="maxHeight100" />
                </a>
                <i
                  class="lni lni-trash text-danger pointer table-action-icon position-absolute ml-n4"
                  @click="imageDeleteConfirm(image.id)"
                ></i>
              </div>

              <div class="bg-light p-3 my-2">
                <form-group label="Add Images">
                  <image-upload
                    :key="imageComponentKey"
                    @files-uploaded="
                      selectedPropertyImages = $event;
                      imageUploading = false;
                    "
                    @files-uploaded-started="imageUploading = true"
                    :multiple="true"
                    :imageCrop="false"
                    acceptedTypes="image/*"
                  />
                  <small class="text-muted font-size-11"
                    >*Max file size is 16MB</small
                  >
                </form-group>

                <b-button
                  class="float-right"
                  @click="saveImages"
                  variant="primary"
                  size="sm"
                  v-if="selectedPropertyImages.length > 0"
                  >Save Images</b-button
                >
                <div style="clear: both"></div>
              </div>
            </b-tab>
            <b-tab title="Files" class="pt-3">
              <table class="table">
                <tr v-for="file in property.property_files" :key="file.id">
                  <td>
                    <a :href="file.url" target="new">
                      {{ file.description }}<br />
                    </a>
                  </td>
                  <td>{{ file.size / 1024 }}kb</td>
                  <td class="text-right text-muted">
                    {{
                      moment(new Date(file.created_at)).format(
                        "DD MMM YYYY, HH:mm:ss"
                      )
                    }}
                  </td>
                  <td class="text-right">
                    <i
                      class="lni lni-trash text-danger pointer table-action-icon"
                      @click="fileDeleteConfirm(file.id)"
                    ></i>
                  </td>
                </tr>
              </table>

              <div class="bg-light p-3 my-2">
                <form-group label="Add Files">
                  <file-upload
                    :key="fileComponentKey"
                    @files-uploaded="
                      selectedPropertyFiles = $event;
                      fileUploading = false;
                    "
                    @files-uploaded-started="fileUploading = true"
                    :multiple="true"
                  />
                  <small class="text-muted font-size-11"
                    >*Max file size is 16MB</small
                  >
                </form-group>
                <b-button
                  class="float-right"
                  @click="saveFiles"
                  variant="primary"
                  size="sm"
                  v-if="selectedPropertyFiles.length > 0"
                  >Save Files</b-button
                >
                <div style="clear: both"></div>
              </div>

              <b-skeleton-img v-if="FileLoading"></b-skeleton-img>

              <div></div>
            </b-tab>
            <b-tab title="Comments" class="pt-3">
              <div
                v-for="comment in property.property_comments"
                :key="comment.id"
                class="card border-bottom p-2 mb-1"
              >
                <p class="nl2br">
                  {{ comment.comment }}
                </p>

                <div class="row">
                  <div class="col-auto">
                    <b-avatar v-if="!comment.user.image"></b-avatar>
                    <b-avatar
                      v-if="comment.user.image"
                      :src="comment.user.image"
                    ></b-avatar>
                  </div>
                  <div class="col">
                    <h6 class="mb-0 mt-1 font-weight-bold">
                      {{ comment.user.name }}
                    </h6>
                    <small class="text-muted">
                      <i class="lni lni-calendar"></i>
                      {{
                        moment(new Date(comment.created_at)).format(
                          "DD MMM YYYY, HH:mm:ss"
                        )
                      }}

                      |

                      {{
                        moment
                          .duration(
                            moment(Date.now()).diff(
                              moment(new Date(comment.created_at)),
                              "seconds"
                            ),
                            "seconds"
                          )
                          .format("Y [year] D [day], H [hour &] m [min]")
                      }}
                      ago
                    </small>
                  </div>
                </div>
              </div>

              <form-base
                :submit-status="submitStatus"
                action="Comment"
                :errors="errors"
                :loading="loading"
                v-on:handleSubmit="createComment"
              >
                <template v-slot:fields>
                  <form-group label="Comment" :required="true">
                    <textarea-autosize
                      class="form-control"
                      v-model="newComment"
                    />
                  </form-group>
                </template>
              </form-base>
            </b-tab>
            <b-tab title="Inventory">
              <b-tabs v-model="tabIndexInventory" class="mt-3">
                <b-tab title="List">
                  <div class="table-responsive">
                    <table class="table table-bordered mt-3">
                      <tr
                        class="bg-dark text-white font-size-11 text-uppercase font-weight-bold"
                      >
                        <td>Item</td>
                        <td>Description</td>
                        <td>Value</td>
                        <td>Serial</td>
                        <td></td>
                      </tr>
                      <tr
                        v-for="inv in property.property_inventory"
                        :key="inv.id"
                      >
                        <td>{{ inv.item }}</td>
                        <td>{{ inv.description }}</td>
                        <td class="font-weight-bold">
                          {{ inv.value | currency }}
                        </td>
                        <td>{{ inv.serial }}</td>
                        <td class="text-right">
                          <a
                            v-b-tooltip.hover
                            title="view"
                            class="text-info mr-1 pointer table-action-icon"
                            @click="
                              selectedInventory = inv;
                              inventoryShow = true;
                            "
                          >
                            <i class="lni lni-search-alt"></i>
                          </a>

                          <a
                            v-b-tooltip.hover
                            title="Edit"
                            class="text-primary mr-1 pointer table-action-icon"
                            @click="
                              selectedInventory = inv;
                              inventoryEdit = true;
                            "
                          >
                            <i class="lni lni-pencil"></i>
                          </a>

                          <span
                            v-b-tooltip.hover
                            style="cursor: pointer"
                            title="Delete"
                            @click="confirmDeleteInv(inv.id)"
                            class="text-danger table-action-icon"
                          >
                            <i class="lni lni-trash"></i>
                          </span>
                        </td>
                      </tr>
                    </table>
                  </div>
                </b-tab>
                <b-tab title="Create">
                  <div class="border p-2 mt-2">
                    <form-base
                      :submit-status="submitStatus"
                      action="Create"
                      :errors="errors"
                      :loading="loading"
                      v-on:handleSubmit="handleSubmitInventory"
                    >
                      <template v-slot:fields>
                        <form-group label="Item" :required="true">
                          <input
                            @blur="$v.newInventory.item.$touch"
                            class="form-control"
                            :class="{
                              'is-invalid': $v.newInventory.item.$error,
                            }"
                            v-model="newInventory.item"
                          />
                          <div
                            class="invalid-feedback"
                            v-if="!$v.newInventory.item.required"
                          >
                            Please enter title
                          </div>
                        </form-group>

                        <form-group label="Description">
                          <textarea-autosize
                            class="form-control"
                            v-model="newInventory.description"
                          />
                        </form-group>

                        <form-group label="Value" :required="true">
                          <input
                            type="number"
                            min="0"
                            @blur="$v.newInventory.value.$touch"
                            class="form-control"
                            :class="{
                              'is-invalid': $v.newInventory.value.$error,
                            }"
                            v-model="newInventory.value"
                          />
                          <div
                            class="invalid-feedback"
                            v-if="!$v.newInventory.value.required"
                          >
                            Please enter amount
                          </div>
                        </form-group>

                        <form-group label="Serial#">
                          <input
                            class="form-control"
                            v-model="newInventory.serial"
                          />
                        </form-group>

                        <form-group label="Notes">
                          <textarea-autosize
                            class="form-control"
                            v-model="newInventory.notes"
                          />
                        </form-group>
                      </template>
                    </form-base>
                  </div>
                </b-tab>
              </b-tabs>
              <b-alert variant="info" class="mt-5" show>
                <b-media>
                  <template #aside>
                    <i class="lni lni-bulb font-size-26"></i>
                  </template>
                  <ul>
                    <li>
                      Attach purchase invoice/slip to inventory files for safe
                      keeping and tracking.
                    </li>
                  </ul>
                </b-media>
              </b-alert>
            </b-tab>
            <b-tab title="Expenses">
              <b-tabs v-model="tabIndexExpense" class="mt-3">
                <b-tab title="List">
                  <expenses :propertyId="id" :key="expenseIndex"></expenses>
                </b-tab>
                <b-tab title="Create">
                  <div class="border p-2 mt-2">
                    <form-base
                      :submit-status="submitStatus"
                      action="Create"
                      :errors="errors"
                      :loading="loading"
                      v-on:handleSubmit="handleSubmitExpense"
                    >
                      <template v-slot:fields>
                        <form-group label="Select Contact" :required="true">
                          <v-select
                            label="accountable"
                            :options="contacts"
                            :filterable="false"
                            @search="getContacts"
                            v-model="selectedContact"
                          >
                            <template slot="no-options">
                              Type to search contacts
                            </template>
                            <template slot="option" slot-scope="c">
                              <div v-if="c">
                                {{ c.name }}
                                {{ c.category }}<br />
                                {{ c.email_address }}
                              </div>
                            </template>
                            <template slot="selected-option" slot-scope="c">
                              <div v-if="c">
                                {{ c.name }}
                              </div>
                            </template>
                          </v-select>

                          <small
                            v-if="$v.selectedContact.$error"
                            class="text-danger"
                          >
                            Please select contact
                          </small>
                        </form-group>

                        <form-group label="Reference">
                          <input
                            placeholder="contact invoice number"
                            class="form-control"
                            v-model="newExpense.invoice_number"
                          />
                        </form-group>

                        <form-group label="Title" :required="true">
                          <input
                            placeholder="short description"
                            @blur="$v.newExpense.item.$touch"
                            class="form-control"
                            :class="{ 'is-invalid': $v.newExpense.item.$error }"
                            v-model="newExpense.item"
                          />
                          <div
                            class="invalid-feedback"
                            v-if="!$v.newExpense.item.required"
                          >
                            Please enter title
                          </div>
                        </form-group>

                        <form-group label="Description">
                          <textarea-autosize
                            class="form-control"
                            v-model="newExpense.description"
                          />
                        </form-group>

                        <form-group label="Amount" :required="true">
                          <input
                            type="number"
                            min="0"
                            @blur="$v.newExpense.amount.$touch"
                            class="form-control"
                            :class="{
                              'is-invalid': $v.newExpense.amount.$error,
                            }"
                            v-model="newExpense.amount"
                          />
                          <div
                            class="invalid-feedback"
                            v-if="!$v.newExpense.amount.required"
                          >
                            Please enter amount
                          </div>
                        </form-group>

                        <form-group label="Date" :required="true">
                          <b-form-datepicker
                            :class="{
                              'is-invalid': $v.newExpense.expense_date.$error,
                            }"
                            v-model="newExpense.expense_date"
                            class="mb-2"
                          ></b-form-datepicker>
                          <div
                            class="invalid-feedback"
                            v-if="$v.newExpense.expense_date.required"
                          >
                            Please select date
                          </div>
                        </form-group>

                        <form-group
                          v-if="property.property_inventory"
                          label="Inventory Item"
                        >
                          <b-form-select
                            v-model="newExpense.property_inventory_id"
                            :options="property.property_inventory"
                            text-field="item"
                            value-field="id"
                          ></b-form-select>
                        </form-group>

                        <form-group label="Tenant Damage">
                          <b-form-checkbox
                            value="1"
                            unchecked-value="0"
                            v-model="newExpense.is_tenant_damage"
                            switch
                            >Tenant caused damage</b-form-checkbox
                          >
                        </form-group>

                        <form-group
                          label="Tenant"
                          v-if="newExpense.is_tenant_damage == 1"
                        >
                          <b-form-select
                            v-model="selectedExpenseTenant"
                            :options="expenseTenants"
                            text-field="name"
                            value-field="id"
                          ></b-form-select>
                        </form-group>

                        <form-group
                          label="Bill tenant"
                          v-if="newExpense.is_tenant_damage == 1"
                        >
                          <b-form-checkbox
                            value="1"
                            unchecked-value="0"
                            v-model="newExpense.charge_tenant"
                            switch
                            >Generate Invoice on tenants
                            account</b-form-checkbox
                          >
                        </form-group>

                        <form-group
                          label="Email invoice"
                          v-if="
                            newExpense.is_tenant_damage == 1 &&
                            newExpense.charge_tenant == 1
                          "
                        >
                          <b-form-checkbox
                            value="1"
                            unchecked-value="0"
                            v-model="newExpense.email_to_tenant"
                            switch
                            >Email invoice to tenant</b-form-checkbox
                          >
                        </form-group>
                      </template>
                    </form-base>
                  </div>
                </b-tab>
              </b-tabs>
            </b-tab>
            <b-tab title="Invoices">
              <invoices :propertyId="id"></invoices>
            </b-tab>
            <b-tab title="Leases">
              <leases :propertyId="id"></leases>
            </b-tab>
          </b-tabs>
        </b-card>
      </b-col>
    </b-row>

    <b-modal
      size="lg"
      title="Inventory Item"
      v-model="inventoryShow"
      hide-footer
    >
      <b-tabs>
        <b-tab title="Details">
          <table class="table mt-2" v-if="inventoryShow">
            <table-row label="Item" :noBorder="true">
              {{ selectedInventory.item }}
            </table-row>
            <table-row label="Description">
              {{ selectedInventory.description }}
            </table-row>
            <table-row label="Value">
              {{ selectedInventory.value }}
            </table-row>
            <table-row label="Serial">
              {{ selectedInventory.serial }}
            </table-row>
            <table-row label="Notes">
              {{ selectedInventory.notes }}
            </table-row>
            <table-row icon="lni-database" :noBorder="true" />

            <table-row label="Id">
              {{ selectedInventory.id }}
            </table-row>

            <table-row label="Created">
              {{
                moment(new Date(selectedInventory.created_at)).format(
                  "DD MMM YYYY, HH:mm:ss"
                )
              }}
            </table-row>
            <table-row label="Updated">
              {{
                moment(new Date(selectedInventory.updated_at)).format(
                  "DD MMM YYYY, HH:mm:ss"
                )
              }}
            </table-row>
          </table>
        </b-tab>
        <b-tab
          class="pt-3"
          title="Images"
          v-if="selectedInventory && selectedInventory.images"
        >
          <b-skeleton-img v-if="ImageLoading"></b-skeleton-img>

          <v-lazy-image
            @intersect="ImageLoading = true"
            @load="ImageLoading = false"
            v-if="selectedImage != null"
            :src="selectedImage"
            class="img-fluid border"
          />
          <div></div>

          <div
            class="border d-inline-block p-1 m-1"
            v-for="image in selectedInventory.images"
            :key="image.id"
          >
            <a href="#" @click="selectedImage = image.large">
              <v-lazy-image :src="image.small" class="maxHeight100" />
            </a>
            <i
              class="lni lni-trash text-danger pointer table-action-icon position-absolute ml-n4"
              @click="
                inventoryImageDeleteConfirm(image.id, selectedInventory.id)
              "
            ></i>
          </div>

          <div v-if="loading">
            <b-spinner
              variant="primary"
              style="width: 1rem; height: 1rem"
              label="Large Spinner"
            ></b-spinner>
          </div>

          <div class="bg-light p-3 my-2">
            <form-group label="Add Images">
              <image-upload
                :key="imageComponentKey"
                @files-uploaded="
                  selectedPropertyInventoryImages = $event;
                  imageUploading = false;
                "
                @files-uploaded-started="imageUploading = true"
                :multiple="true"
                :imageCrop="false"
                acceptedTypes="image/*"
              />
              <small class="text-muted font-size-11"
                >*Max file size is 16MB</small
              >
            </form-group>
            <b-button
              class="float-right"
              @click="saveImagesInventory"
              variant="primary"
              size="sm"
              v-if="selectedPropertyInventoryImages.length > 0"
              >Save Images</b-button
            >
            <div style="clear: both"></div>
          </div>
        </b-tab>

        <b-tab title="Files" class="pt-3">
          <b-skeleton-img v-if="FileLoading"></b-skeleton-img>

          <div></div>
          <b-spinner v-if="loading"></b-spinner>
          <table class="table" v-if="selectedInventory && !loading">
            <tr v-for="file in selectedInventory.files" :key="file.id">
              <td>
                <a :href="file.url" target="new">
                  {{ file.description }}<br />
                </a>
              </td>
              <td>{{ file.size / 1024 }}kb</td>
              <td class="text-right text-muted">
                {{
                  moment(new Date(file.created_at)).format(
                    "DD MMM YYYY, HH:mm:ss"
                  )
                }}
              </td>
              <td class="text-right">
                <i
                  class="lni lni-trash text-danger pointer table-action-icon"
                  @click="inventoryDeleteFile(file.id, selectedInventory.id)"
                ></i>
              </td>
            </tr>
          </table>

          <div class="p-3 my-2 bg-light">
            <form-group label="Add Files">
              <file-upload
                :key="fileComponentKey"
                @files-uploaded="
                  selectedPropertyInventoryFiles = $event;
                  fileUploading = false;
                "
                @files-uploaded-started="fileUploading = true"
                :multiple="true"
              />
              <small class="text-muted font-size-11"
                >*Max file size is 16MB</small
              >
            </form-group>
            <b-button
              class="float-right"
              @click="saveInventoryFiles(selectedInventory.id)"
              variant="primary"
              size="sm"
              v-if="selectedPropertyInventoryFiles.length > 0"
              >Save Files</b-button
            >

            <div style="clear: both"></div>
          </div>
        </b-tab>

        <b-tab title="Expenses">
          <span v-if="selectedInventory && selectedInventory.expenses">
            <table class="table table-borderd mt-3">
              <tr
                v-for="expense in selectedInventory.expenses"
                :key="expense.id"
              >
                <td>
                  {{
                    moment(new Date(expense.expense_date)).format(
                      "DD MMM YYYY, HH:mm:ss"
                    )
                  }}
                </td>

                <td>
                  {{ expense.item }}
                </td>
                <td>
                  {{ expense.amount }}
                </td>
              </tr>
            </table>
          </span>
        </b-tab>
      </b-tabs>
    </b-modal>

    <b-modal size="lg" title="Edit Item" v-model="inventoryEdit" hide-footer>
      <form-base
        :submit-status="submitStatus"
        action="Update"
        :errors="errors"
        :loading="loading"
        v-on:handleSubmit="handleSubmitInventoryEdit"
      >
        <template v-slot:fields>
          <form-group label="Item" :required="true">
            <input
              @blur="$v.selectedInventory.item.$touch"
              class="form-control"
              :class="{
                'is-invalid': $v.selectedInventory.item.$error,
              }"
              v-model="selectedInventory.item"
            />
            <div
              class="invalid-feedback"
              v-if="!$v.selectedInventory.item.required"
            >
              Please enter title
            </div>
          </form-group>

          <form-group label="Description">
            <textarea-autosize
              class="form-control"
              v-model="selectedInventory.description"
            />
          </form-group>

          <form-group label="Value" :required="true">
            <input
              type="number"
              min="0"
              @blur="$v.selectedInventory.value.$touch"
              class="form-control"
              :class="{
                'is-invalid': $v.selectedInventory.value.$error,
              }"
              v-model="selectedInventory.value"
            />
            <div
              class="invalid-feedback"
              v-if="!$v.selectedInventory.value.required"
            >
              Please enter amount
            </div>
          </form-group>

          <form-group label="Serial#">
            <input class="form-control" v-model="selectedInventory.serial" />
          </form-group>

          <form-group label="Notes">
            <textarea-autosize
              class="form-control"
              v-model="selectedInventory.notes"
            />
          </form-group>
        </template>
      </form-base>
    </b-modal>

    <b-modal
      :hide-footer="true"
      :title="$route.meta.title"
      size="lg"
      v-model="sidebarToggle"
      :no-close-on-route-change="true"
      @hidden="$router.push({ name: 'properties-show', params: { id: id } })"
      :lazy="true"
    >
      <router-view @propertyUpdated="propertyUpdated()"></router-view>
    </b-modal>
  </div>
</template>

<script>
import propertyRepo from "@/api/repos/propertyRepo";
import contactRepo from "@/api/repos/contactRepo";
import tableRow from "@/components/common/TableRow";
import { required } from "vuelidate/lib/validators";
import fileUpload from "@/components/common/FileUpload";
import imageUpload from "@/components/common/ImageUpload";
import formGroup from "@/components/common/FormGroup";
import formBase from "@/components/common/FormBase";
import invoices from "@/views/modules/properties/invoices";
import expenses from "@/views/modules/properties/expenses";
import leases from "@/views/modules/properties/leases";
import BarChart from "../../../components/charts/bar";

export default {
  props: ["id"],

  components: {
    tableRow,
    fileUpload,
    formBase,
    imageUpload,
    formGroup,
    invoices,
    expenses,
    leases,
    BarChart,
  },

  mounted() {
    this.getProperty();
  },

  data() {
    return {
      selectedContact:null,
      contacts:[],
      expenseIndex: 1,
      sidebarToggle: false,
      inventoryEdit: false,
      inventoryShow: false,
      selectedInventory: null,
      newInventory: {},
      tabIndex: 0,
      tabIndexInventory: 0,
      tabIndexExpense: 0,
      newComment: null,
      newExpense: {
        expense_date: "",
        tenant: null,
      },
      selectedImage: null,
      submitStatus: null,
      errors: null,
      loading: false,
      property: {},
      selectedPropertyFiles: [],
      selectedPropertyInventoryFiles: [],
      fileComponentKey: 1,
      fileUploading: false,
      FileLoading: false,
      selectedPropertyImages: [],
      selectedPropertyInventoryImages: [],
      imageComponentKey: 1,
      searchActivated:false,
      imageUploading: false,
      ImageLoading: false,
      expenseTenants: [],
      selectedExpenseTenant: null,
      chartReady: false,
      chartData: {
        labels: [],
        datasets: [],
      },
      chartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          yAxes: [
            {
              ticks: {
                min: 0,
              },
            },
          ],
        },
      },
    };
  },

  validations: {
    selectedContact:{
      required
    },
    newExpense: {      
      item: {
        required,
      },
      amount: {
        required,
      },
      expense_date: {
        required,
      },
    },
    newInventory: {
      item: {
        required,
      },
      value: {
        required,
      },
    },
    selectedInventory: {
      item: {
        required,
      },
      value: {
        required,
      },
    },
  },

  methods: {
     getContacts(search) {
      this.searchActivated = true;
      if (search.length > 1) {
        clearTimeout(this.delayTimer);
        this.delayTimer = setTimeout(() => {
          this.contactsSearch(search);
        }, 500);
      }
    },
     async contactsSearch(search) {
      try {
        let { data } = await contactRepo.contacts(
          1,
          25,
          "id",
          "asc",
          search,
          null
        );
        this.contacts = data.contacts.data;
        
      } finally {
        this.loading = false;
      }
    },

    async getProperty() {
      try {
        this.loading = true;
        let { data } = await propertyRepo.get(this.id);
        this.property = data.property;
        data.tenants.forEach((x) => {
          this.expenseTenants.push(x.tenant);
        });

        let tempChartData = {
          labels: ["2 Ago", "Previous", "Current Month"],
          datasets: [
            {
              backgroundColor: "rgb(255, 0, 0,0.5)",
              label: "invoices",
              data: [
                data.monthly_invoiced_third_month,
                data.monthly_invoiced_second_month,
                data.monthly_invoiced,
              ],
            },
            {
              backgroundColor: "rgb(0,255, 0,0.5)",
              label: "payments",
              data: [
                data.monthly_payments_third_month,
                data.monthly_payments_second_month,
                data.monthly_payments,
              ],
            },
            {
              backgroundColor: "rgb(255, 255, 0,0.5)",
              label: "expenses",
              data: [
                data.monthly_expenses_third_month,
                data.monthly_expenses_second_month,
                data.monthly_expenses,
              ],
            },
          ],
        };
        this.chartData = tempChartData;
        this.chartReady = true;
      } catch (error) {
        console.log(error);
      } finally {
        this.loading = false;
      }
    },
    async saveFiles() {
      if (this.fileUploading) {
        this.$root.$bvToast.toast("Files Uploading", {
          title: "Please wait for files to upload",
          autoHideDelay: 2000,
          variant: "warning",
        });
        return false;
      } else {
        try {
          await propertyRepo.files(this.id, {
            property_files: this.selectedPropertyFiles.split(","),
          });
          this.$root.$bvToast.toast("Uploaded", {
            title: "Uploaded",
            autoHideDelay: 3000,
            variant: "success",
          });
          this.selectedPropertyFiles = [];
          this.fileComponentKey++;
          this.getProperty();
          this.tabIndex = 2;
        } catch (Ex) {
          this.$root.$bvToast.toast("Whoops", {
            title: "Something went wrong",
            autoHideDelay: 3000,
            variant: "danger",
          });
        }
      }
    },
    async saveImages() {
      if (this.imageUploading) {
        this.$root.$bvToast.toast("Images Uploading", {
          title: "Please wait for files to upload",
          autoHideDelay: 2000,
          variant: "warning",
        });
        return false;
      } else {
        try {
          await propertyRepo.files(this.id, {
            property_images: this.selectedPropertyImages.split(","),
          });
          this.$root.$bvToast.toast("Uploaded", {
            title: "Uploaded",
            autoHideDelay: 3000,
            variant: "success",
          });
          this.selectedPropertyImages = [];
          this.imageComponentKey++;
          this.getProperty();
          this.tabIndex = 1;
        } catch (Ex) {
          this.$root.$bvToast.toast("Whoops", {
            title: "Something went wrong",
            autoHideDelay: 3000,
            variant: "danger",
          });
        }
      }
    },
    async saveImagesInventory() {
      if (this.imageUploading) {
        this.$root.$bvToast.toast("Images Uploading", {
          title: "Please wait for files to upload",
          autoHideDelay: 2000,
          variant: "warning",
        });
        return false;
      } else {
        try {
          await propertyRepo.imagesInventory(
            this.id,
            this.selectedInventory.id,
            {
              property_inventory_images: this.selectedPropertyInventoryImages.split(
                ","
              ),
            }
          );
          this.$root.$bvToast.toast("Uploaded", {
            title: "Uploaded",
            autoHideDelay: 3000,
            variant: "success",
          });
          this.selectedPropertyInventoryImages = [];
          this.imageComponentKey++;
          await this.getProperty();
          this.property.property_inventory.forEach((x) => {
            if (x.id == this.selectedInventory.id) {
              this.selectedInventory = x;
            }
          });
        } catch (Ex) {
          this.$root.$bvToast.toast("Whoops", {
            title: "Something went wrong",
            autoHideDelay: 3000,
            variant: "danger",
          });
        }
      }
    },
    async deleteProp(id) {
      this.isBusy = true;
      try {
        await propertyRepo.delete(id);
        this.$router.push({ name: "properties" });
      } catch (error) {
        console.log(error);
      } finally {
        this.isBusy = false;
      }
    },
    confirmDelete(id) {
      this.$bvModal
        .msgBoxConfirm("Please confirm that you want to delete the property.", {
          title: "Please Confirm",
          size: "sm",
          buttonSize: "sm",
          okVariant: "danger",
          okTitle: "YES",
          cancelTitle: "NO",
          footerClass: "p-2",
          hideHeaderClose: false,
          centered: true,
        })
        .then((confirm) => (confirm ? this.deleteProp(id) : false))
        .catch((error) => console.log(error));
    },
    async createComment() {
      this.loading = true;
      this.submitStatus = "PENDING";

      if (this.newComment == null) {
        alert("Enter comment");
        this.submitStatus = "ERROR";
        this.loading = false;
      } else {
        try {
          await propertyRepo.createComment({
            comment: this.newComment,
            property_id: this.id,
          });
          this.getProperty();
          this.tabIndex = 3;
          this.newComment = null;
          this;
        } catch (error) {
          console.log(error);
          if (error.response.status === 422) {
            this.errors = error.response.data.errors;
            this.$root.$bvToast.toast("Form Errors", {
              title: "Errors on form data",
              autoHideDelay: 3000,
              variant: "danger",
            });
          } else {
            this.$root.$bvToast.toast("Something Went Wrong", {
              title: error.response.data.message,
              autoHideDelay: 3000,
              variant: "danger",
            });
          }
        } finally {
          this.loading = false;
          this.submitStatus = null;
        }
      }
    },

    async handleSubmitExpense() {
      this.loading = true;
      this.submitStatus = "PENDING";
      this.$v.$touch();

      this.newExpense.property_id = this.id;

      if (this.newExpense.is_tenant_damage == 1) {
        if (this.selectedExpenseTenant == null) {
          this.$root.$bvToast.toast("Tenant Required", {
            title: "Errors on form data",
            autoHideDelay: 3000,
            variant: "danger",
          });
          return false;
        } else {
          this.newExpense.tenant_id = this.selectedExpenseTenant;
        }
      } else {
        this.newExpense.is_tenant_damage == 0;
      }
      if (this.$v.newExpense.$invalid || this.$v.selectedContact.$invalid) {
        this.loading = false;
        this.submitStatus = null;
      } else {
        try {
          this.newExpense.contact_id = this.selectedContact.id
          await propertyRepo.createExpense(this.newExpense);
          this.newExpense = {
            expense_date: "",
            tenant: null,
          };
          this.getProperty();
          this.tabIndex = 5;
          this.tabIndexExpense = 0;
          this.expenseIndex = this.expenseIndex + 1;
        } catch (error) {
          console.log(error);
          if (error.response.status === 422) {
            this.errors = error.response.data.errors;
            console.log(this.errors);
            this.$root.$bvToast.toast("Form Errors", {
              title: "Errors on form data",
              autoHideDelay: 3000,
              variant: "danger",
            });
          }
        } finally {
          this.loading = false;
          this.submitStatus = null;
        }
      }
    },
    async handleSubmitInventory() {
      this.loading = true;
      this.submitStatus = "PENDING";
      this.$v.$touch();
      if (this.$v.newInventory.$invalid) {
        this.loading = false;
        this.submitStatus = null;
      } else {
        try {
          this.newInventory.property_id = this.id;
          await propertyRepo.createInventory(this.newInventory);
          this.getProperty();
          this.tabIndex = 4;
          this.tabIndexInventory = 0;
          this.newInventory = {};
        } catch (error) {
          console.log(error);
          if (error.response.status === 422) {
            this.errors = error.response.data.errors;
            console.log(this.errors);
            this.$root.$bvToast.toast("Form Errors", {
              title: "Errors on form data",
              autoHideDelay: 3000,
              variant: "danger",
            });
          }
        } finally {
          this.loading = false;
          this.submitStatus = null;
        }
      }
    },
    async handleSubmitInventoryEdit() {
      this.loading = true;
      this.submitStatus = "PENDING";
      this.$v.$touch();
      if (this.$v.selectedInventory.$invalid) {
        this.loading = false;
        this.submitStatus = null;
      } else {
        try {
          this.selectedInventory.property_id = this.id;
          await propertyRepo.updateInventory(this.selectedInventory);
          this.inventoryEdit = false;
          this.getProperty();
          this.tabIndex = 4;
          this.selectedInventory = {};
        } catch (error) {
          console.log(error);
          if (error.response.status === 422) {
            this.errors = error.response.data.errors;
            console.log(this.errors);
            this.$root.$bvToast.toast("Form Errors", {
              title: "Errors on form data",
              autoHideDelay: 3000,
              variant: "danger",
            });
          }
        } finally {
          this.loading = false;
          this.submitStatus = null;
        }
      }
    },
    confirmDeleteInv(id) {
      this.$bvModal
        .msgBoxConfirm("Please confirm that you want to delete the property.", {
          title: "Please Confirm",
          size: "sm",
          buttonSize: "sm",
          okVariant: "danger",
          okTitle: "YES",
          cancelTitle: "NO",
          footerClass: "p-2",
          hideHeaderClose: false,
          centered: true,
        })
        .then((confirm) => (confirm ? this.deleteInventory(id) : false))
        .catch((error) => console.log(error));
    },
    async deleteInventory(id) {
      this.isBusy = true;
      try {
        await propertyRepo.deleteInventory({
          id: id,
          property_id: this.id,
        });
        this.property.property_inventory = this.property.property_inventory.filter(
          (item) => item.id !== id
        );
      } catch (error) {
        console.log(error);
      } finally {
        this.isBusy = false;
      }
    },
    propertyUpdated() {
      this.$bvToast.toast("Collection Updated", {
        title: "Success",
        autoHideDelay: 3000,
        variant: "success",
      });
      this.sidebarToggle = false;
      this.getProperty();
    },

    async deleteFile(id) {
      this.loading = true;
      try {
        await propertyRepo.fileDelete(this.id, id);
        this.getProperty();
        this.tabIndex = 2;
      } catch (error) {
        this.$root.$bvToast.toast("Whoops", {
          title: "Something went wrong",
          autoHideDelay: 3000,
          variant: "danger",
        });
      } finally {
        this.loading = false;
      }
    },
    fileDeleteConfirm(id) {
      this.$bvModal
        .msgBoxConfirm("Please confirm that you want to delete the file.", {
          title: "Please Confirm",
          size: "sm",
          buttonSize: "sm",
          okVariant: "danger",
          okTitle: "YES",
          cancelTitle: "NO",
          footerClass: "p-2",
          hideHeaderClose: false,
          centered: true,
        })
        .then((confirm) => (confirm ? this.deleteFile(id) : false))
        .catch((error) => console.log(error));
    },

    async deleteImage(id) {
      this.loading = true;
      try {
        await propertyRepo.imageDelete(this.id, id);
        this.getProperty();
        this.tabIndex = 1;
        this.selectedImage = null;
      } catch (error) {
        this.$root.$bvToast.toast("Whoops", {
          title: "Something went wrong",
          autoHideDelay: 3000,
          variant: "danger",
        });
      } finally {
        this.loading = false;
      }
    },
    imageDeleteConfirm(id) {
      this.$bvModal
        .msgBoxConfirm("Please confirm that you want to delete the image.", {
          title: "Please Confirm",
          size: "sm",
          buttonSize: "sm",
          okVariant: "danger",
          okTitle: "YES",
          cancelTitle: "NO",
          footerClass: "p-2",
          hideHeaderClose: false,
          centered: true,
        })
        .then((confirm) => (confirm ? this.deleteImage(id) : false))
        .catch((error) => console.log(error));
    },

    async deleteInventoryImage(id, inventoryId) {
      this.loading = true;
      try {
        await propertyRepo.inventoryImageDelete(this.id, inventoryId, id);
        await this.getProperty();
        this.property.property_inventory.forEach((x) => {
          if (x.id == this.selectedInventory.id) {
            this.selectedInventory = x;
          }
        });
        this.tabIndex = 4;
        this.selectedImage = null;
      } catch (error) {
        this.$root.$bvToast.toast("Whoops", {
          title: "Something went wrong",
          autoHideDelay: 3000,
          variant: "danger",
        });
      } finally {
        this.loading = false;
      }
    },
    inventoryImageDeleteConfirm(id, inventoryId) {
      this.$bvModal
        .msgBoxConfirm("Please confirm that you want to delete the image.", {
          title: "Please Confirm",
          size: "sm",
          buttonSize: "sm",
          okVariant: "danger",
          okTitle: "YES",
          cancelTitle: "NO",
          footerClass: "p-2",
          hideHeaderClose: false,
          centered: true,
        })
        .then((confirm) =>
          confirm ? this.deleteInventoryImage(id, inventoryId) : false
        )
        .catch((error) => console.log(error));
    },

    async inventoryDeleteFile(id, inventoryId) {
      this.loading = true;
      try {
        await propertyRepo.inventoryFileDelete(this.id, inventoryId, id);
        await this.getProperty();
        this.property.property_inventory.forEach((x) => {
          if (x.id == this.selectedInventory.id) {
            this.selectedInventory = x;
          }
        });
        this.tabIndex = 4;
      } catch (error) {
        this.$root.$bvToast.toast("Whoops", {
          title: "Something went wrong",
          autoHideDelay: 3000,
          variant: "danger",
        });
      } finally {
        this.loading = false;
      }
    },
    inventoryFileDeleteConfirm(id, inventoryId) {
      this.$bvModal
        .msgBoxConfirm("Please confirm that you want to delete the file.", {
          title: "Please Confirm",
          size: "sm",
          buttonSize: "sm",
          okVariant: "danger",
          okTitle: "YES",
          cancelTitle: "NO",
          footerClass: "p-2",
          hideHeaderClose: false,
          centered: true,
        })
        .then((confirm) => (confirm ? this.deleteFile(id, inventoryId) : false))
        .catch((error) => console.log(error));
    },

    async saveInventoryFiles(inventoryId) {
      if (this.fileUploading) {
        this.$root.$bvToast.toast("Files Uploading", {
          title: "Please wait for files to upload",
          autoHideDelay: 2000,
          variant: "warning",
        });
        return false;
      } else {
        try {
          await propertyRepo.filesInventory(this.id, inventoryId, {
            property_inventory_files: this.selectedPropertyInventoryFiles.split(
              ","
            ),
          });
          this.$root.$bvToast.toast("Uploaded", {
            title: "Uploaded",
            autoHideDelay: 3000,
            variant: "success",
          });
          this.selectedPropertyInventoryFiles = [];
          this.fileComponentKey++;
          await this.getProperty();
          this.property.property_inventory.forEach((x) => {
            if (x.id == this.selectedInventory.id) {
              this.selectedInventory = x;
            }
          });
          this.tabIndex = 4;
        } catch (Ex) {
          this.$root.$bvToast.toast("Whoops", {
            title: "Something went wrong",
            autoHideDelay: 3000,
            variant: "danger",
          });
        }
      }
    },
  },

  watch: {
    inventoryShow: function () {
      this.selectedImage = null;
    },
    newExpense: function (val) {
      if (val.is_tenant_damage == 0) {
        this.newExpense.tenant = null;
      }
    },
    $route: {
      immediate: true,
      handler: function (newVal) {
        this.sidebarToggle = newVal.meta && newVal.meta.sidebarToggle;
      },
    },
  },
};
</script>
